import React from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Camera,
    Shield,
    Bell,
    Globe,
    ExternalLink,
    ChevronRight,
    Save
} from 'lucide-react';

export default function Settings() {
    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <Sidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Account Settings"
                    subtitle="Manage your profile information and account preferences"
                >
                    <button className="flex items-center gap-3 py-2.5 px-8 bg-blue-500 text-white rounded-xl text-[14px] font-black shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all active:scale-95">
                        <Save size={18} />
                        Save Changes
                    </button>
                </Header>

                <div className="max-w-5xl mb-10 pb-10">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        {/* Profile Section */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl shadow-gray-100/50 p-10 text-center relative overflow-hidden group">
                                <div className="absolute top-0 inset-x-0 h-32 bg-blue-500 transition-all group-hover:h-36"></div>
                                <div className="relative pt-8">
                                    <div className="relative inline-block mb-6">
                                        <div className="w-32 h-32 rounded-full border-4 border-white shadow-2xl overflow-hidden bg-gray-50 transform transition-all group-hover:scale-105">
                                            <img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah" alt="Sarah Johnson" className="w-full h-full object-cover" />
                                        </div>
                                        <button className="absolute bottom-0 right-0 w-10 h-10 bg-blue-500 rounded-full border-4 border-white flex items-center justify-center text-white shadow-xl hover:bg-blue-600 transition-all active:scale-90">
                                            <Camera size={16} />
                                        </button>
                                    </div>
                                    <h3 className="text-[24px] font-black text-[#111827] mb-1">Dr. Sarah Johnson</h3>
                                    <p className="text-gray-400 font-bold text-[14px] uppercase tracking-widest mb-8">Senior Math Teacher</p>

                                    <div className="flex flex-col gap-3 text-left">
                                        <SettingRow icon={Mail} label="Email Address" value="sarah.j@school.edu" />
                                        <SettingRow icon={Phone} label="Contact No." value="+1 (234) 567 890" />
                                        <SettingRow icon={MapPin} label="Location" value="Main Campus, Block B" />
                                    </div>

                                    <button className="w-full mt-10 py-4 border border-gray-100 rounded-2xl text-[14px] font-black text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-3 transition-all group">
                                        View public profile
                                        <ExternalLink size={16} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Settings */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Security Section */}
                            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-8 border-b border-gray-50 bg-[#F8FAFC]/50 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                                            <Shield size={18} className="text-blue-600" />
                                        </div>
                                        <h3 className="text-[18px] font-black text-[#111827]">Security & Authentication</h3>
                                    </div>
                                    <ChevronRight size={18} className="text-gray-400" />
                                </div>
                                <div className="p-8 space-y-6">
                                    <div className="flex items-center justify-between border-b border-gray-50 pb-6 last:border-0 last:pb-0">
                                        <div>
                                            <p className="text-[15px] font-black text-[#111827] mb-1">Two-Factor Authentication</p>
                                            <p className="text-[13px] font-bold text-gray-400">Secure your account with 2FA protection</p>
                                        </div>
                                        <div className="w-12 h-6 bg-blue-500 rounded-full relative cursor-pointer shadow-inner">
                                            <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full transition-all"></div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[15px] font-black text-[#111827] mb-1">Change Account Password</p>
                                            <p className="text-[13px] font-bold text-gray-400">Regularly update your password for safety</p>
                                        </div>
                                        <button className="px-5 py-2.5 rounded-xl border border-gray-100 text-[13px] font-black text-gray-700 hover:bg-gray-50 transition-all">Update</button>
                                    </div>
                                </div>
                            </div>

                            {/* Notifications */}
                            <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-8 border-b border-gray-50 bg-[#F8FAFC]/50 flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm">
                                            <Bell size={18} className="text-amber-500" />
                                        </div>
                                        <h3 className="text-[18px] font-black text-[#111827]">Manage Notifications</h3>
                                    </div>
                                    <ChevronRight size={18} className="text-gray-400" />
                                </div>
                                <div className="p-8 grid grid-cols-2 gap-8">
                                    <NotificationToggle label="Email Alerts" active={true} />
                                    <NotificationToggle label="Mobile Push" active={true} />
                                    <NotificationToggle label="System Logs" active={false} />
                                    <NotificationToggle label="SMS Reports" active={false} />
                                </div>
                            </div>

                            {/* External Apps */}
                            <div className="bg-blue-500 rounded-[32px] p-8 text-white flex items-center justify-between shadow-xl shadow-blue-100">
                                <div className="flex items-center gap-6">
                                    <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-3xl border border-white/20">
                                        <Globe size={24} />
                                    </div>
                                    <div>
                                        <h3 className="text-[18px] font-black mb-1">External API Keys</h3>
                                        <p className="text-white/60 text-[13px] font-bold">Connect with Canvas, Google, or Microsoft</p>
                                    </div>
                                </div>
                                <button className="px-6 py-3 bg-white text-blue-500 rounded-xl text-[14px] font-black hover:bg-gray-50 transition-all shadow-xl shadow-blue-900/10">Configure</button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function SettingRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 p-3 rounded-2xl hover:bg-blue-50 transition-all group cursor-default">
            <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-500 group-hover:text-white transition-all">
                <Icon size={14} />
            </div>
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</p>
                <p className="text-[14px] font-black text-gray-700">{value}</p>
            </div>
        </div>
    );
}

function NotificationToggle({ label, active }) {
    return (
        <div className="flex items-center justify-between group cursor-pointer">
            <span className="text-[14px] font-black text-gray-700 group-hover:text-blue-500 transition-colors uppercase tracking-tight">{label}</span>
            <div className={`w-10 h-5 rounded-full relative transition-all ${active ? 'bg-blue-500' : 'bg-gray-200'}`}>
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${active ? 'right-0.5' : 'left-0.5'}`}></div>
            </div>
        </div>
    );
}
